// Canonical engine wire emit for a WhatsApp newsletter. Both engines
// (chrome extension + baileys pod) project their raw store shape
// — WAWeb's NewsletterMetadata for chrome, baileys-sdk's newsletter
// metadata for baileys — to this shape at their handler boundary.
// v1's /v1/newsletters mapper is a pure 1:1 projection.
//
// The raw WAWeb NewsletterMetadata (with WaWid id, micro-second
// creationTime, attestation/suspension flags, etc.) lives engine-side
// per #74. The slim variant is the ONLY wire form bt-common exposes.
//
// Identity field name differs by surface:
//   - single-newsletter emits (waGetNewsletter / waCreateNewsletter)
//     carry the typed `newsletterId`.
//   - list rows (waListNewsletters) carry the generic `chatId`, so every
//     list tool (chats / contacts / groups / newsletters) shares one
//     row-identity field. Both hold the same JID, e.g. `12345@newsletter`.

import { z } from 'zod';
import { WireJidSchema } from '../../contract/wire-identity';

/** Caller's relationship to the newsletter. */
export const WaNewsletterMembershipSchema = z.enum(['owner', 'subscriber']);
export type WaNewsletterMembership = z.infer<typeof WaNewsletterMembershipSchema>;

/** Newsletter visibility. */
export const WaNewsletterPrivacySchema = z.enum(['public', 'private']);
export type WaNewsletterPrivacy = z.infer<typeof WaNewsletterPrivacySchema>;

// Fields shared by every newsletter wire emit, EXCEPT the identity key.
const newsletterCommonShape = {
  name: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  /** Unix-seconds creation timestamp (engine converts micros → seconds). */
  creationTime: z.number().nullable().optional(),
  subscribers: z.number().int().nullable().optional(),
  /** The full channel URL (not the bare invite code). */
  invite: z.string().nullable().optional(),
  verification: z.enum(['VERIFIED', 'UNVERIFIED']).nullable().optional(),
};

/** Single-newsletter emit (waGetNewsletter / waCreateNewsletter). */
export const BtSlimNewsletterSchema = z.object({
  /** Newsletter JID, e.g. `12345@newsletter`. */
  newsletterId: WireJidSchema,
  ...newsletterCommonShape,
});
export type BtSlimNewsletter = z.infer<typeof BtSlimNewsletterSchema>;

/** List-row emit (waListNewsletters). Keyed by the generic `chatId` so
 *  every list tool shares one row-identity field. */
export const BtSlimNewsletterListItemSchema = z.object({
  /** Newsletter JID, e.g. `12345@newsletter`. */
  chatId: WireJidSchema,
  ...newsletterCommonShape,
});
export type BtSlimNewsletterListItem = z.infer<typeof BtSlimNewsletterListItemSchema>;
